import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

const Login = () => {
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(email, password, organizationSlug, false);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="bg-white">
      <section className="min-h-[calc(100vh-80px-220px)] py-16 lg:py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: copy */}
            <div className="hidden lg:block pt-6">
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-blue-600">Sign in</span>
              <h1 className="mt-4 text-5xl xl:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                Welcome back.
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-md">
                Access your organization dashboard and stay on top of every piece of customer feedback in real time.
              </p>

              <ul className="mt-10 space-y-4">
                {[
                  'Real-time per-employee feedback feed',
                  'Department-level analytics and trends',
                  'Bulk employee management & CSV export',
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                    <span className="text-slate-700">{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-12 pt-8 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Don't have an account yet?{' '}
                  <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-700">
                    Register your business →
                  </Link>
                </p>
              </div>
            </div>

            {/* Right: form */}
            <div>
              <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-blue-100/40 p-8 lg:p-10">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">Sign in to your dashboard</h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Enter your organization identifier and credentials below.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Organization</label>
                    <input
                      type="text"
                      data-testid="login-org-slug"
                      value={organizationSlug}
                      onChange={(e) => setOrganizationSlug(e.target.value)}
                      className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="e.g., your-company-name"
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1.5">Your organization identifier (slug).</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      data-testid="login-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      placeholder="admin@yourcompany.com"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <label className="block text-sm font-medium text-slate-700">Password</label>
                      <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-700">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        data-testid="login-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-slate-300 rounded-xl px-4 py-3 pr-12 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div data-testid="login-error" className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="login-submit"
                    disabled={isLoading}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold shadow-lg shadow-blue-600/30 transition"
                  >
                    {isLoading ? 'Signing in…' : 'Sign in'}
                    {!isLoading && (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                    )}
                  </button>
                </form>

                <p className="lg:hidden mt-6 text-center text-sm text-slate-500">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-700">
                    Register
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
